const CLICK_KINDS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;
const CLICKS_KEY = 'svq_ad_clicks';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_CLICKS = 10;

type ClickKind = (typeof CLICK_KINDS)[number];

interface StoredClick {
  kind: ClickKind;
  value: string;
  clicked_at: string;
  landing_url: string;
  reported: boolean;
}

export type AdClick = Omit<StoredClick, 'reported'>;

export interface Attribution {
  clicks: AdClick[];
}

export function rememberAdClicks(url: URL, now = new Date()): void {
  const params = url.searchParams;
  const arrivals = CLICK_KINDS.flatMap((kind) => {
    const value = params.get(kind);
    return value ? [{ kind, value, clicked_at: now.toISOString(), landing_url: url.href, reported: false }] : [];
  });
  if (arrivals.length === 0) return;

  const known = storedClicks(now);
  const fresh = arrivals.filter((arrival) => !known.some((click) => isSameClick(click, arrival)));
  writeClicks(keepFirstAndLatest([...known, ...fresh]));
}

export function attribution(now = new Date()): Attribution {
  return attributionOf(storedClicks(now));
}

export function markAdClicksReported(): void {
  writeClicks(storedClicks(new Date()).map((click) => ({ ...click, reported: true })));
}

export function unreportedAttribution(now = new Date()): Attribution | null {
  const clicks = storedClicks(now).filter((click) => !click.reported);
  if (clicks.length === 0) return null;

  return attributionOf(clicks);
}

function attributionOf(clicks: StoredClick[]): Attribution {
  return { clicks: clicks.map((click) => toAdClick(click)) };
}

function storedClicks(now: Date): StoredClick[] {
  return readClicks().filter((click) => now.getTime() - Date.parse(click.clicked_at) < RETENTION_MS);
}

function toAdClick({ kind, value, clicked_at, landing_url }: StoredClick): AdClick {
  return { kind, value, clicked_at, landing_url };
}

function isSameClick(a: AdClick, b: AdClick): boolean {
  return a.kind === b.kind && a.value === b.value;
}

function keepFirstAndLatest(clicks: StoredClick[]): StoredClick[] {
  if (clicks.length <= MAX_CLICKS) return clicks;

  return [clicks[0], ...clicks.slice(-(MAX_CLICKS - 1))];
}

function readClicks(): StoredClick[] {
  try {
    return (JSON.parse(localStorage.getItem(CLICKS_KEY) ?? 'null') as StoredClick[] | null) ?? [];
  } catch {
    return [];
  }
}

function writeClicks(clicks: StoredClick[]): void {
  try {
    localStorage.setItem(CLICKS_KEY, JSON.stringify(clicks));
  } catch {
    return;
  }
}
